import { createModel } from 'ibiza'
import orderBy from 'lodash.orderby'
import { standardWithTime } from '@lib/date-time-formats'
import { format, parseISO } from 'date-fns'
import { addTicketsToBasket, removeTicketsFromBasket } from '@lib/spektrix/index'
import { showBookingError } from '@lib/booking-error-handler'
import { buildSeatList, resolveSeatId } from './seat-list'

const modelFunction = (_state, props) => ({
  event: null,
  instance: null,
  get instanceDescription () {
    return `${this.event?.name} - ${format(
      parseISO(this.instance.start),
      standardWithTime
    )}`
  },

  plan: null,
  showTicketTypes: false,
  instanceSeats: null,
  availableSeats: null,
  seatActions: {},

  updatingBasket: false,
  mealDealSelection: null,
  mealDealsToRemove: null,

  currentBasket: [],
  get unreservedTicketsInBasket () {
    return this.currentBasket
      .filter(t => !t.seat)
      .map(t => ({ id: t.id, type: t.type.id }))
  },

  addingToBasket: false,

  get seats () {
    // Early return if no instance seats
    if (!this.instanceSeats) return []

    // Cache the result if nothing has changed
    // Include seatActions values in cache key to detect changes
    const seatActionsKey = JSON.stringify(this.seatActions)
    const cacheKey = `${this.instanceSeats?.length}_${!!this.availableSeats}_${seatActionsKey}_${this.currentBasket.length}`
    if (this._cacheKey === cacheKey && this._cachedSeats) {
      return this._cachedSeats
    }

    // buildSeatList merges static geometry with dynamic state and keeps the
    // reference identity of unchanged seats stable (so React.memo(Seat) holds
    // and a click re-renders one seat, not the whole plan).
    const seatArray = buildSeatList({
      instanceSeats: this.instanceSeats,
      seatActions: this.seatActions,
      availableSeats: this.availableSeats,
      currentBasket: this.currentBasket
    })

    this._cacheKey = cacheKey
    this._cachedSeats = seatArray

    return seatArray
  },

  get reservedSeating () {
    // Check if plan type is Reserved
    if (this.plan?.type === 'Reserved') return true
    
    // Check if plan type is Group and has areas
    if (this.plan?.type === 'Group' && this.plan?.areas?.length > 0) {
      // Check if the areas have seats (reserved) or are unreserved
      const hasReservedAreas = this.plan.areas.some(area => 
        area.type === 'Reserved' || 
        (area.seats && Object.keys(area.seats).length > 0)
      )
      if (hasReservedAreas) return true
    }
    
    // Check if plan has any seats defined (indicates reserved seating)
    if (this.plan?.seats && Object.keys(this.plan.seats).length > 0) {
      return true
    }
    
    return false
  },

  get unreservedSeating () {
    return this.plan?.type === 'Unreserved'
  },

  get groupUnreservedSeating () {
    // Check if this is a Group plan with unreserved areas
    if (this.plan?.type === 'Group' && this.plan?.areas?.length > 0) {
      // Check if all areas are unreserved
      const allUnreserved = this.plan.areas.every(area => 
        area.type === 'Unreserved' && 
        (!area.seats || Object.keys(area.seats).length === 0)
      )
      return allUnreserved
    }
    return false
  },

  get onlyDeleting () {
    return (
      Object.keys(this.seatActions).length > 0 &&
      this.seatsInBasket.length === 0
    )
  },

  get seatsInBasket () {
    // Cache the result to prevent unnecessary recalculation
    const seatActionsKey = JSON.stringify(this.seatActions)
    const cacheKey = `${this._cacheKey}_seatsInBasket_${this.currentBasket.length}_${seatActionsKey}`
    if (this._seatsInBasketCacheKey === cacheKey && this._cachedSeatsInBasket) {
      return this._cachedSeatsInBasket
    }

    const result = orderBy(
      this.seats
        .filter(seat => seat.inBasket)
        .map(seat => ({
          ...seat,
          ticket:
            this.seatActions[seat.id]?.ticket ||
            this.currentBasket.find(i => i.seat.id === seat.id)
        })) || [],
      ['row', 'number'],
      ['asc', num => parseInt(num, 10)]
    )

    // Cache the result
    this._seatsInBasketCacheKey = cacheKey
    this._cachedSeatsInBasket = result

    return result
  },

  get readyToAddToBasket () {
    // Check if we have tickets to remove first
    if (this.ticketsToRemove.length > 0) {
      return true
    }
    
    // Check if we have ticket type modifications
    if (this.ticketsToModify.length > 0) {
      return true
    }
    
    // Check if we have meal deal changes (additions or removals)
    if (this.mealDealSelection && this.mealDealSelection.count > 0) {
      return true
    }
    
    // Check if we have meal deals to remove
    if (this.mealDealsToRemove && this.mealDealsToRemove.length > 0) {
      return true
    }
    
    // Check if we have seat actions
    if (Object.keys(this.seatActions).length > 0 && this.showTicketTypes) {
      // Count seats that are not marked for removal
      const activeSeats = this.seatsInBasket.filter(s => !this.seatActions[s.id]?.removeFromBasket)
      return (
        this.ticketTypesInBasket.length > 0 &&
        this.ticketTypesInBasket.length === activeSeats.length
      )
    }
    
    return false
  },

  // Single stable entry point for a seat tap. The toggle branch lives here (not
  // in the view) because it depends on the current basket — so the view can
  // pass one stable callback and React.memo(Seat) holds. Mirrors the previous
  // per-seat inline handlers: a seat already in the basket toggles its removal
  // state; everything else goes through handleClick.
  toggleSeat (seatId) {
    // Ibiza may inject the model as the first arg, so resolve the real id from
    // `arguments` the same way handleClick/handleRemoveTicket do — otherwise
    // String(modelProxy) becomes "[object Object]" and the wrong key is written.
    const id = resolveSeatId(arguments)
    if (!id) return

    const alreadyInBasket = this.currentBasket.some(
      t => String(t.seat?.id) === id
    )
    if (alreadyInBasket) {
      this.handleRemoveTicket(id)
    } else {
      this.handleClick(id)
    }
  },

  handleClick (seatId) {
    // In Ibiza models, methods may be called with the model as context
    let actualSeatId = seatId
    
    // If the first argument looks like a proxy/model object and we have more arguments
    if (typeof seatId === 'object' && seatId !== null && arguments.length > 1) {
      actualSeatId = arguments[1]
    }
    
    // Ensure we have a valid seat ID
    if (!actualSeatId || typeof actualSeatId === 'object' || typeof actualSeatId === 'function') {
      return
    }
    
    seatId = String(actualSeatId)
    
    // Create a new seatActions object to trigger state update
    const newSeatActions = { ...this.seatActions }
    
    // Initialize seatActions for this seat if needed
    if (!newSeatActions[seatId]) {
      newSeatActions[seatId] = {}
    }
    
    // Check if we're toggling off a selected seat
    if (newSeatActions[seatId].addToBasket) {
      delete newSeatActions[seatId]
      // Reassign to trigger update
      this.seatActions = newSeatActions
      // Clear the cache to force immediate update
      this._cacheKey = null
      this._cachedSeats = null
      this._seatsInBasketCacheKey = null
      this._cachedSeatsInBasket = null
      // remove the related ticket type if there is one
      const index = this.ticketTypesInBasket.findIndex(
        t => t.seat.id === seatId
      )
      if (index !== -1) {
        this.ticketTypesInBasket.splice(index, 1)
      }
      return
    }

    // Check if this seat is already in the current basket (not just selected)
    const alreadyInBasket = this.currentBasket.find(t => String(t.seat?.id) === seatId)
    
    if (alreadyInBasket) {
      newSeatActions[seatId] = { removeFromBasket: true }
    } else {
      if (newSeatActions[seatId].removeFromBasket) {
        delete newSeatActions[seatId]
      } else {
        // Update the seat action
        newSeatActions[seatId] = { addToBasket: true }
      }
    }
    
    // Reassign the entire object to trigger state update
    this.seatActions = newSeatActions
    
    // Clear the cache to force immediate update
    this._cacheKey = null
    this._cachedSeats = null
    this._seatsInBasketCacheKey = null
    this._cachedSeatsInBasket = null
    
    // Auto-adjust meal deals if user pattern suggests it
    this.checkAutoAdjustMealDeals()

    if (this.seatsInBasket.length === 0) {
      this.showTicketTypes = false
    }
  },

  checkAutoAdjustMealDeals () {
    // Check if we should automatically adjust meal deals
    // This is called after seats are added/removed
    
    // Count current tickets (both in basket and newly selected)
    const currentTicketCount = this.seatsInBasket.filter(s => !s.markedForRemoval).length
    
    // Get meal deals already in basket for this instance
    const mealDealsInBasket = this.currentBasket.filter(t => {
      const eventName = t.event?.name || ''
      return eventName.toLowerCase().includes('dinner offer') || 
             eventName.toLowerCase().includes('meal deal')
    })
    
    // Count meal deals that are NOT marked for removal
    const activeMealDeals = mealDealsInBasket.filter(md => {
      // Check if this meal deal is marked for removal
      const mealDealId = md.id
      return !this.mealDealsToRemove || !this.mealDealsToRemove.includes(mealDealId)
    }).length
    
    // If user has meal deals, adjust to match ticket count
    if (mealDealsInBasket.length > 0) {
      if (currentTicketCount > activeMealDeals) {
        // Need to add more meal deals
        this.suggestMealDealAdjustment = {
          currentTickets: currentTicketCount,
          currentMealDeals: activeMealDeals,
          suggestedToAdd: currentTicketCount - activeMealDeals
        }
      } else if (currentTicketCount < activeMealDeals) {
        // Need to remove excess meal deals
        const excessCount = activeMealDeals - currentTicketCount
        const mealDealsToRemove = mealDealsInBasket
          .filter(md => !this.mealDealsToRemove || !this.mealDealsToRemove.includes(md.id))
          .slice(0, excessCount)
          .map(md => md.id)
        
        // Update the meal deals to remove
        if (this.mealDealsToRemove) {
          this.mealDealsToRemove = [...this.mealDealsToRemove, ...mealDealsToRemove]
        } else {
          this.mealDealsToRemove = mealDealsToRemove
        }
        
        // Clear any add suggestions
        this.suggestMealDealAdjustment = null
      } else {
        // Count matches, no adjustment needed
        this.suggestMealDealAdjustment = null
      }
    } else {
      this.suggestMealDealAdjustment = null
    }
  },

  handleRemoveTicket (seatId) {
    // This is specifically for the ticket type selection view
    // Always mark for removal, don't toggle
    
    // In Ibiza models, methods may be called with the model as context
    let actualSeatId = seatId
    
    // If the first argument looks like a proxy/model object and we have more arguments
    if (typeof seatId === 'object' && seatId !== null && arguments.length > 1) {
      actualSeatId = arguments[1]
    }
    
    seatId = String(actualSeatId)
    
    const newSeatActions = { ...this.seatActions }
    
    // Check if already marked for removal, if so, unmark it
    if (newSeatActions[seatId]?.removeFromBasket) {
      delete newSeatActions[seatId]
    } else {
      // Mark for removal
      newSeatActions[seatId] = { removeFromBasket: true }
    }
    
    // Reassign to trigger update
    this.seatActions = newSeatActions
    
    // Clear caches
    this._cacheKey = null
    this._cachedSeats = null
    this._seatsInBasketCacheKey = null
    this._cachedSeatsInBasket = null
    
    // Check if meal deals need adjustment
    this.checkAutoAdjustMealDeals()
  },

  async removeTicketImmediately (seatId) {
    // Find the ticket in the current basket
    const ticket = this.currentBasket.find(t => String(t.seat?.id) === String(seatId))
    
    if (!ticket) {
      return
    }
    
    try {
      this.updatingBasket = true
      
      // Remove the ticket from the basket immediately
      await removeTicketsFromBasket([ticket.id])
      
      // Update the current basket to reflect the removal
      const updatedBasket = this.currentBasket.filter(t => t.id !== ticket.id)
      this.currentBasket = updatedBasket
      
      // Clear any seat actions for this seat
      const newSeatActions = { ...this.seatActions }
      delete newSeatActions[seatId]
      this.seatActions = newSeatActions
      
      // Clear caches
      this._cacheKey = null
      this._cachedSeats = null
      this._seatsInBasketCacheKey = null
      this._cachedSeatsInBasket = null
      
      // If no more seats in basket, go back to seat selection
      if (this.seatsInBasket.length === 0) {
        this.showTicketTypes = false
      }
      
      // Trigger a refresh of the basket data
      if (this._mutate) {
        this._mutate()
      }
    } catch (error) {
      alert('Failed to remove ticket from basket. Please try again.')
    } finally {
      this.updatingBasket = false
    }
  },

  get ticketsToRemove () {
    return this.currentBasket
      .filter(t => t.seat && this.seatActions[t.seat.id]?.removeFromBasket)
      .map(t => t.id)
  },

  get ticketsToAdd () {
    return this.seatsInBasket
      .filter(t => !t.ticket.id)
      .map(seat => {
        let tkt = {
          type: seat.ticket.type.id,
          instance: this.instance.spektrix_v3_id || this.instance.id
        }
        if (seat.id) {
          tkt.seat = seat.id
        }
        return tkt
      })
  },

  get ticketsToModify () {
    const toModify = []
    
    for (const seatInBasket of this.seatsInBasket) {
      // Find the original ticket in currentBasket
      const originalTicket = this.currentBasket.find(
        t => t.seat?.id === seatInBasket.id
      )
      
      if (!originalTicket) {
        continue
      }
      
      // Check if the ticket type has changed
      const originalTypeId = originalTicket.ticket?.type?.id || originalTicket.type?.id
      const newTypeId = seatInBasket.ticket?.type?.id
      
      if (originalTypeId !== newTypeId && newTypeId) {
        toModify.push({
          ticketId: originalTicket.id,
          originalType: originalTypeId,
          newType: newTypeId,
          seat: seatInBasket
        })
      }
    }
    
    return toModify
  },

  async addToBasket () {
    let success = false
    try {
      this.addingToBasket = true

      // First, handle direct removals
      if (this.ticketsToRemove.length > 0) {
        await removeTicketsFromBasket(this.ticketsToRemove)
      }

      // Then, handle modifications (remove old, add new)
      // IMPORTANT: Capture the modifications before any removals
      const ticketsToModifySnapshot = [...this.ticketsToModify]
      
      if (ticketsToModifySnapshot.length > 0) {
        // Remove the old tickets
        const ticketIdsToRemove = ticketsToModifySnapshot.map(t => t.ticketId)
        
        try {
          await removeTicketsFromBasket(ticketIdsToRemove)
          
          // Wait for removal to be processed
          await new Promise(resolve => setTimeout(resolve, 1500))
          
          // Add back with new ticket types using the snapshot
          const ticketsToReAdd = ticketsToModifySnapshot.map(t => ({
            type: t.newType,
            instance: this.instance.spektrix_v3_id || this.instance.id,
            seat: t.seat.id
          }))
          
          await addTicketsToBasket(ticketsToReAdd)
        } catch (modifyError) {
          throw new Error(`Failed to modify ticket types: ${modifyError.message || JSON.stringify(modifyError)}`)
        }
      }

      // Finally, handle new additions
      if (this.ticketsToAdd.length > 0) {
        await addTicketsToBasket(this.ticketsToAdd)
      }

      // Handle meal deal removal
      if (this.mealDealsToRemove && this.mealDealsToRemove.length > 0) {
        try {
          await removeTicketsFromBasket(this.mealDealsToRemove)
        } catch (mealDealError) {
          alert('Failed to remove meal deals. Please try again.')
        }
      }

      // Add meal deals if selected
      if (this.mealDealSelection && this.mealDealSelection.count > 0) {
        const { count, mealDeal, mealDealPlan, ticketType, ticketTypes } = this.mealDealSelection
        const mealDealTickets = []

        for (let i = 0; i < count; i++) {
          const tt = ticketTypes?.[i] || ticketType
          mealDealTickets.push({
            instance: mealDeal.spektrix_v3_id || mealDeal.id,
            seatingPlan: mealDealPlan.seatingPlanId || mealDealPlan.spektrix_v3_id || mealDealPlan.spektrix_id || mealDealPlan.id,
            type: tt.value
          })
        }
        
        if (mealDealTickets.length > 0) {
          try {
            await addTicketsToBasket(mealDealTickets)
          } catch (mealDealError) {
            // If meal deals fail, still proceed but warn the user
            alert('Your tickets have been added to the basket, but the meal deals could not be added. This may be because meal deals are not available for this performance.')
          }
        }
      }

      // TODO: handle modify ticket types (need a live event to test this)

      success = true
    } catch (error) {
      // Use global showBookingError if available (for priority booking error handling)
      if (window.showBookingError) {
        window.showBookingError(error)
      } else {
        showBookingError(error)
      }
      return false
    } finally {
      this.addingToBasket = false
      if (success) {
        document.location.href = '/basket'
      }
    }
  },

  get ticketTypesInBasket () {
    return this.seatsInBasket.filter(s => s.ticket?.type && !this.seatActions[s.id]?.removeFromBasket)
  },

  ...props
})

const useCustomModel = mountPath => createModel(mountPath, modelFunction)

export default useCustomModel
